import {
  applyBonus,
  type BonusKind,
  catchBonuses,
  type Effects,
  type FallingBonus,
  maybeSpawn,
  NO_EFFECTS,
  stepBonuses,
  tickEffects,
} from './bonuses'
import { type GameConfig, gameConfig, WORLD_HEIGHT, WORLD_WIDTH } from './config'
import { createPaddle, type Paddle, stepPaddle } from './paddle'
import { type Shuttle, stepShuttle } from './shuttle'

/**
 * Вся игра — чистая функция step(state, input, dt, rng). Ни canvas, ни времени,
 * ни Math.random внутри: поэтому физика и баланс проверяются юнит-тестами, а
 * rAF, отрисовка и ввод живут отдельно в loop.ts.
 */

export type Phase = 'idle' | 'playing' | 'over'

export interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  /** Сколько секунд ещё жить. */
  life: number
}

export interface Wind {
  /** Боковое ускорение, знак — направление. */
  force: number
  /** Сколько игровых секунд ещё дует. */
  left: number
}

export interface GameState {
  phase: Phase
  shuttle: Shuttle
  paddle: Paddle
  score: number
  /** Число отбоев — по нему, а не по очкам, растёт сложность. */
  hits: number
  /** Точных ударов подряд. */
  combo: number
  wind: Wind | null
  /**
   * Промах случился, идёт замедление: сколько реальных секунд осталось до
   * game over. null — промаха не было.
   */
  slowMoLeft: number | null
  /** Длительность текущей партии, с. */
  elapsed: number
  particles: Particle[]
  bonuses: FallingBonus[]
  effects: Effects
  /** Монет поймано за партию; в кошелёк их кладёт UI по game over. */
  coins: number
}

export interface GameInput {
  /** Куда тянется ракетка, в координатах поля. */
  targetX: number
}

export type GameEvent =
  | { type: 'hit'; score: number; points: number; combo: number; perfect: boolean }
  | { type: 'wind'; direction: -1 | 1 }
  | { type: 'bonus'; kind: BonusKind }
  | { type: 'shield' }
  | { type: 'miss' }
  | { type: 'over'; score: number; hits: number; coins: number; duration: number }

/** Источник случайности [0, 1). Подменяется в тестах. */
export type Rng = () => number

function servedShuttle(cfg: GameConfig): Shuttle {
  return { x: WORLD_WIDTH / 2, y: cfg.shuttle.serveY, vx: 0, vy: 0 }
}

export function createGame(cfg: GameConfig = gameConfig): GameState {
  return {
    phase: 'idle',
    shuttle: servedShuttle(cfg),
    paddle: createPaddle(),
    score: 0,
    hits: 0,
    combo: 0,
    wind: null,
    slowMoLeft: null,
    elapsed: 0,
    particles: [],
    bonuses: [],
    effects: NO_EFFECTS,
    coins: 0,
  }
}

/** Подача: новая партия с нуля, ракетка остаётся там, где палец. */
export function startGame(state: GameState, cfg: GameConfig = gameConfig): GameState {
  return { ...createGame(cfg), phase: 'playing', paddle: state.paddle }
}

/** Множитель гравитации: темп растёт с каждым отбоем, до потолка. */
export function gravityMultiplier(hits: number, cfg: GameConfig = gameConfig): number {
  const { gravityStep, maxGravityMultiplier } = cfg.difficulty
  return Math.min(1 + gravityStep * hits, maxGravityMultiplier)
}

/** Полуширина ракетки: после порога сужается с каждым отбоем, до минимума. */
export function paddleHalfWidth(hits: number, cfg: GameConfig = gameConfig): number {
  const { narrowFromHits, narrowStep, minHalfWidth } = cfg.difficulty
  const narrowed = cfg.paddle.halfWidth - Math.max(0, hits - narrowFromHits) * narrowStep
  return Math.max(narrowed, minHalfWidth)
}

/** Полуширина с учётом бонуса «широкая ракетка» — её и видит игрок, и ловит отбой. */
export function currentHalfWidth(state: GameState, cfg: GameConfig = gameConfig): number {
  const base = paddleHalfWidth(state.hits, cfg)
  if (state.effects.wide > 0) return base * cfg.bonus.wideFactor
  if (state.effects.narrow > 0) {
    return Math.max(base * cfg.bonus.narrowFactor, cfg.bonus.narrowMinHalfWidth)
  }
  return base
}

/** Сколько очков стоит удар при такой серии точных ударов. */
export function pointsForHit(combo: number, cfg: GameConfig = gameConfig): number {
  return 1 + Math.min(Math.floor(combo / cfg.combo.step), cfg.combo.maxBonus)
}

export function step(
  state: GameState,
  input: GameInput,
  dt: number,
  rng: Rng,
  cfg: GameConfig = gameConfig,
): { state: GameState; events: GameEvent[] } {
  const events: GameEvent[] = []
  const halfWidth = currentHalfWidth(state, cfg)
  // Ракетка живёт в реальном времени и во время slow-mo: палец не замедлишь.
  const paddle = stepPaddle(state.paddle, input.targetX, dt, cfg.paddle.followRate, halfWidth)

  if (state.phase !== 'playing') {
    const particles = stepParticles(state.particles, dt, cfg)
    return { state: { ...state, paddle, particles }, events }
  }

  const missed = state.slowMoLeft !== null
  // Замедление после промаха важнее бонусного: это уже финал партии.
  const timeScale = missed
    ? cfg.slowMo.timeScale
    : state.effects.slow > 0
      ? cfg.bonus.slowScale
      : state.effects.fast > 0
        ? cfg.bonus.fastScale
        : 1
  const gameDt = dt * timeScale
  const particles = stepParticles(state.particles, gameDt, cfg)
  // Эффекты тикают в реальном времени: «замедление на 5 с» — это 5 с игрока.
  let effects = tickEffects(state.effects, dt)
  let bonuses = stepBonuses(state.bonuses, gameDt, cfg)
  let coins = state.coins

  let wind = state.wind
  if (wind) {
    const left = wind.left - gameDt
    wind = left > 0 ? { ...wind, left } : null
  }

  const mul = gravityMultiplier(state.hits, cfg)
  const prev = state.shuttle
  let shuttle = stepShuttle(prev, gameDt, {
    gravity: cfg.shuttle.gravity * mul,
    drag: cfg.shuttle.drag,
    radius: cfg.shuttle.radius,
    wallBounce: cfg.shuttle.wallBounce,
    wind: wind?.force ?? 0,
  })
  let { score, hits, combo } = state
  let slowMoLeft = state.slowMoLeft
  const r = cfg.shuttle.radius

  // Проверяем пересечение линии ракетки за шаг, а не положение «внутри»:
  // на пике скорости волан проходит за шаг больше собственного радиуса.
  const crossed =
    !missed && shuttle.vy > 0 && prev.y + r <= cfg.paddle.y && shuttle.y + r >= cfg.paddle.y

  if (crossed && Math.abs(shuttle.x - paddle.x) <= halfWidth + r) {
    const offset = Math.max(-1, Math.min(1, (shuttle.x - paddle.x) / halfWidth))
    const perfect = Math.abs(offset) <= cfg.combo.perfectZone
    combo = perfect ? combo + 1 : 0
    const points = pointsForHit(combo, cfg) * (effects.double > 0 ? 2 : 1)
    score += points
    hits += 1

    shuttle = hitShuttle(shuttle, paddle, offset, mul, rng, cfg)
    events.push({ type: 'hit', score, points, combo, perfect })
    particles.push(...spawnParticles(shuttle.x, cfg.paddle.y, rng, cfg))

    if (!wind && hits >= cfg.wind.fromHits && rng() < cfg.wind.chance) {
      const direction = rng() < 0.5 ? -1 : 1
      wind = { force: direction * cfg.wind.force, left: cfg.wind.duration }
      events.push({ type: 'wind', direction })
    }

    const spawned = maybeSpawn(bonuses.length, hits, rng, cfg)
    if (spawned) bonuses = [...bonuses, spawned]
  } else if (crossed && effects.shield) {
    // Страховка: волан отскакивает от нижнего края, как от ракетки, но без
    // очка и со сбросом серии — прощён промах, а не засчитан удар.
    effects = { ...effects, shield: false }
    combo = 0
    shuttle = hitShuttle(shuttle, { x: shuttle.x, vx: 0 }, 0, mul, rng, cfg)
    events.push({ type: 'shield' })
    particles.push(...spawnParticles(shuttle.x, cfg.paddle.y, rng, cfg))
  } else if (crossed) {
    slowMoLeft = cfg.slowMo.duration
    events.push({ type: 'miss' })
  } else if (slowMoLeft !== null) {
    slowMoLeft -= dt
  }

  // После промаха ловить уже нечего — партия кончается.
  if (!missed && slowMoLeft === null) {
    const { left, caught } = catchBonuses(bonuses, shuttle, cfg)
    bonuses = left
    for (const kind of caught) {
      if (kind === 'coin') coins += 1
      if (kind === 'breaker') combo = 0
      effects = applyBonus(effects, kind, cfg)
      events.push({ type: 'bonus', kind })
    }
  }

  const elapsed = state.elapsed + dt
  const next = {
    ...state,
    shuttle,
    paddle,
    score,
    hits,
    combo,
    wind,
    slowMoLeft,
    elapsed,
    particles,
    bonuses,
    effects,
    coins,
  }

  // Второе условие — страховка: волан ушёл вниз, минуя линию ракетки.
  if ((slowMoLeft !== null && slowMoLeft <= 0) || shuttle.y - r > WORLD_HEIGHT) {
    events.push({ type: 'over', score, hits, coins, duration: elapsed })
    return { state: { ...next, phase: 'over' }, events }
  }

  return { state: next, events }
}

function hitShuttle(
  s: Shuttle,
  paddle: Paddle,
  offset: number,
  gravityMul: number,
  rng: Rng,
  cfg: GameConfig,
): Shuttle {
  const { bounceBase, bounceJitter, aimSpeed, paddleCarry, driftJitter } = cfg.hit
  const jitter = (rng() * 2 - 1) * bounceJitter
  // √mul: при квадратичном сопротивлении высота подъёма почти не меняется, если
  // скорость отбоя растёт как корень из гравитации. Растёт темп, а не высота.
  const vy = -(bounceBase + jitter) * Math.sqrt(gravityMul)
  // Удар краем уводит волан в сторону — это и есть «прицеливание».
  const vx = offset * aimSpeed + paddle.vx * paddleCarry + (rng() * 2 - 1) * driftJitter
  return { x: s.x, y: cfg.paddle.y - cfg.shuttle.radius, vx, vy }
}

function spawnParticles(x: number, y: number, rng: Rng, cfg: GameConfig): Particle[] {
  const { countPerHit, life, speed } = cfg.particles
  return Array.from({ length: countPerHit }, () => {
    // Веер вверх: от −160° до −20°.
    const angle = -Math.PI * (0.11 + rng() * 0.78)
    const v = speed * (0.5 + rng() * 0.5)
    return { x, y, vx: Math.cos(angle) * v, vy: Math.sin(angle) * v, life }
  })
}

function stepParticles(particles: Particle[], dt: number, cfg: GameConfig): Particle[] {
  const g = cfg.shuttle.gravity * 0.5
  return particles
    .map((p) => ({
      x: p.x + p.vx * dt,
      y: p.y + p.vy * dt,
      vx: p.vx,
      vy: p.vy + g * dt,
      life: p.life - dt,
    }))
    .filter((p) => p.life > 0)
}
